import { getFileMimeType } from './getFileMimeType';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

const BLOB_URL_REVOKE_DELAY_MS = 10000;

export const appendTimestamp = (name: string) => {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex === -1) {
    return `${name} - ${new Date().getTime()}`;
  }
  return `${name.slice(0, dotIndex)} - ${new Date().getTime()}${name.slice(
    dotIndex
  )}`;
};

export const base64ToBlobUrl = (base64: string, mimeType: string): string => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  const CHUNK_SIZE = 512;

  for (let offset = 0; offset < byteCharacters.length; offset += CHUNK_SIZE) {
    const slice = byteCharacters.slice(offset, offset + CHUNK_SIZE);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: mimeType });
  return URL.createObjectURL(blob);
};

export const downloadFile = async (
  base64: string,
  fileExtension: string,
  fileName: string,
  openPdfInNewTab: boolean = true
) => {
  const mimeType = getFileMimeType(fileExtension);
  const blobUrl = base64ToBlobUrl(base64, mimeType);
  const timestampedFileName = appendTimestamp(fileName);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (isIOS) {
    try {
      // Create blob from base64
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // iOS Safari doesn't support the download attribute on <a> tags for blob URLs.
      // Use the Web Share API which allows saving files to the device.
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], timestampedFileName, { type: mimeType });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file] });
          return;
        }
      }

      // Fallback for older iOS without Web Share API support: open in new tab
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
    } catch (error) {
      // AbortError means user cancelled the share sheet - not an error
      if ((error as Error)?.name === 'AbortError') return;

      // Fallback to opening in new tab
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = blobUrl;
      } else {
        alert(Dictionary[DICTIONARY.General].allowPopups);
      }
    }
  } else {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = timestampedFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

export const downloadAudio = async (audioSrc: string, name: string) => {
  try {
    const response = await fetch(audioSrc, {
      method: 'GET',
      headers: {
        Accept: 'audio/mpeg',
      },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch audio file');
    }
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = appendTimestamp(`${name}.mp3`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading audio:', error);
    alert('שגיאה בהורדת הקובץ. אנא נסה שוב.');
  }
};
